import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, CircularProgress, Tooltip, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignAdminModal from './AssignAdminModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { HotelService } from '../api/services/hotelService';
import { UserService } from '../api/services/userService';

export default function ManageHotelAdminsModal({ open, onClose, hotelId, setNotification }) {
    const activeTenantId = JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1';
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [removeDialog, setRemoveDialog] = useState({ open: false, userId: null });
    const [error, setError] = useState(null);

    const fetchAdmins = useCallback(async () => {
        if (!hotelId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await UserService.getAll({ hotel: hotelId, noPagination: true });
            setAdmins(response.data || response || []);
        } catch (err) {
            console.error('Failed to fetch admins:', err);
            setError('Could not load assigned administrators.');
        } finally {
            setLoading(false);
        }
    }, [hotelId]);

    useEffect(() => {
        if (open && hotelId) {
            fetchAdmins();
        } else {
            setAdmins([]);
        }
    }, [open, hotelId, fetchAdmins]);

    const handleSaveAssign = async (payload) => {
        await HotelService.manageAdmin(hotelId, activeTenantId, payload);
        const actionText = payload.action === 'Invite' ? 'invited' : 'assigned';
        setNotification({ open: true, message: `Administrator ${actionText} successfully!`, severity: 'success' });
        fetchAdmins();
    };

    const confirmRemove = async () => {
        if (!removeDialog.userId) return;
        try {
            await HotelService.manageAdmin(hotelId, activeTenantId, { action: 'Remove', userId: removeDialog.userId });
            setNotification({ open: true, message: 'Admin privileges revoked successfully!', severity: 'success' });
            fetchAdmins();
        } catch (err) {
            setNotification({ open: true, message: err?.response?.data?.message || err?.message || 'Failed to remove admin', severity: 'error' });
        } finally {
            setRemoveDialog({ open: false, userId: null });
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                onClose();
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminPanelSettingsIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">Manage Property Administrators</Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setAssignModalOpen(true)}
                    size="small"
                >
                    Assign / Invite Admin
                </Button>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : admins.length > 0 ? (
                                admins.map((admin) => (
                                    <TableRow key={admin.id} hover>
                                        <TableCell>{admin.name}</TableCell>
                                        <TableCell>{admin.email}</TableCell>
                                        <TableCell>{admin.role?.name || admin.role || 'Admin'}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Revoke Access" arrow>
                                                <IconButton color="error" onClick={() => setRemoveDialog({ open: true, userId: admin.id })}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No administrators assigned to this property yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <AssignAdminModal
                    open={assignModalOpen}
                    onClose={() => setAssignModalOpen(false)}
                    onSave={handleSaveAssign}
                    existingAdmins={admins}
                />

                <ConfirmDeleteModal
                    open={removeDialog.open}
                    onClose={() => setRemoveDialog({ open: false, userId: null })}
                    onConfirm={confirmRemove}
                    itemName="admin access for this user"
                />
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
